import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/** A visible alternative to touch/trackpad scrolling for each day's venues. */
export default function AvailabilityVenueScroller({ label, count, children }: {
  label: string; count: number; children: React.ReactNode;
}) {
  const rail = useRef<HTMLDivElement>(null);
  const id = useId();
  const [position, setPosition] = useState({ left: 0, max: 0 });
  const measure = () => {
    const element = rail.current;
    if (element) setPosition({ left: element.scrollLeft, max: Math.max(0, element.scrollWidth - element.clientWidth) });
  };
  useEffect(() => {
    const element = rail.current;
    if (!element) return;
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [count]);
  const move = (direction: number) => {
    const element = rail.current;
    if (!element) return;
    const card = element.firstElementChild as HTMLElement | null;
    const step = card ? card.getBoundingClientRect().width + 16 : element.clientWidth;
    const page = Math.max(1, Math.floor(element.clientWidth / step));
    element.scrollBy({ left: direction * step * page,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  return <>
    <div id={id} ref={rail} onScroll={measure} tabIndex={0} role="region" aria-label={`Restaurants for ${label}`}
      className="restaurant-card-scroller flex gap-4 overflow-x-auto pb-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600">
      {children}
    </div>
    {position.max > 1 && <div className="venue-scroll-controls flex items-center gap-3 mt-2">
      <button type="button" aria-label={`Previous restaurants for ${label}`} aria-controls={id} disabled={position.left <= 1} onClick={() => move(-1)} className="venue-scroll-button"><ChevronLeft size={20} aria-hidden="true" /></button>
      <input type="range" min={0} max={100} step="any" value={position.max ? Math.min(100, Math.max(0, position.left / position.max * 100)) : 0}
        aria-label={`Browse restaurants for ${label}`} aria-controls={id}
        aria-valuetext={`${Math.round(position.max ? position.left / position.max * 100 : 0)}% through ${count} restaurants`}
        className="venue-scroll-range flex-1 min-w-0" onChange={event => {
          const element = rail.current;
          if (element) {
            const max = element.scrollWidth - element.clientWidth;
            const left = Number(event.target.value) / 100 * max;
            // Keep the controlled input in sync before the browser emits its change event.
            setPosition({ left, max });
            element.scrollTo({ left, behavior: 'auto' });
          }
        }} />
      <button type="button" aria-label={`Next restaurants for ${label}`} aria-controls={id} disabled={position.left >= position.max - 1} onClick={() => move(1)} className="venue-scroll-button"><ChevronRight size={20} aria-hidden="true" /></button>
    </div>}
  </>;
}
